import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../Logo/Logo';
import './Header.css';

const Header: React.FC = () => {
  const location = useLocation();

  // Detectar preferência inicial do utilizador
  const getInitialTheme = () => {
    // 1. Primeiro: verificar localStorage (preferência salva pelo usuário)
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme === 'dark';
    }

    // 2. Segundo: verificar preferência do sistema (default)
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return true;
    }

    // 3. Fallback: light mode
    return false;
  };

  const [isDark, setIsDark] = React.useState(getInitialTheme);
  const [isVisible, setIsVisible] = React.useState(false);
  const [isAtTop, setIsAtTop] = React.useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);



  // Ativar transições de tema após carregamento completo
  useEffect(() => {
         const enableThemeTransitions = () => {
       // Aguardar um pouco para garantir que tudo carregou
       setTimeout(() => {
         document.body.classList.add('theme-transitions');
       }, 500);
     };

    if (document.readyState === 'complete') {
      enableThemeTransitions();
    } else {
      window.addEventListener('load', enableThemeTransitions);
      return () => window.removeEventListener('load', enableThemeTransitions);
    }
  }, []);

  useEffect(() => {
    const el = document.getElementById('themeToggle') as HTMLInputElement | null;
    if (!el) return;

    const setTheme = (dark: boolean, fromSystem = false) => {
      setIsDark(dark);

      // Só guardar no localStorage se não for uma mudança do sistema
      if (!fromSystem) {
        localStorage.setItem('theme', dark ? 'dark' : 'light');
      }

      // Definir data-theme para outros componentes detectarem
      document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');

      if (dark) {
        document.documentElement.style.setProperty('--bg', '#0b0b10');
        document.documentElement.style.setProperty('--card', '#111218');
        document.documentElement.style.setProperty('--muted', '#1a1b23');
        document.documentElement.style.setProperty('--text', '#E8E8F1');
        document.documentElement.style.setProperty('--sub', '#B7B8C7');
        document.documentElement.style.setProperty('--grid', '#151621');
        document.documentElement.style.setProperty('--border', '#232536');
        document.documentElement.style.setProperty('--ghost-bg', '#0f1017');
        document.documentElement.style.setProperty('--ghost-border', '#2a2b37');
        document.documentElement.style.setProperty('--ghost-color', '#E8E8F1');
        document.documentElement.style.setProperty('--nav', '#BFC1CF');
        /* não mexemos no background/transparência aqui */
        document.documentElement.style.setProperty('--nav-shadow', 'rgba(0,0,0,.35)');
        // Adicionar classe dark ao body para o footer
        document.body.classList.add('dark');
      } else {
        document.documentElement.style.setProperty('--bg', '#fff');
        document.documentElement.style.setProperty('--card', '#ffffff');
        document.documentElement.style.setProperty('--muted', '#eef1f7');
        document.documentElement.style.setProperty('--text', '#0b0b10');
        document.documentElement.style.setProperty('--sub', '#4b4d63');
        document.documentElement.style.setProperty('--grid', '#eaeef7');
        document.documentElement.style.setProperty('--border', '#e1e4ef');
        /* ghost button on light: use darker bg so text stays readable */
        document.documentElement.style.setProperty('--ghost-bg', '#ffffff');
        document.documentElement.style.setProperty('--ghost-border', '#cfd5e6');
        document.documentElement.style.setProperty('--ghost-color', '#0b0b10');
        document.documentElement.style.setProperty('--nav', '#333333');
        /* não mexemos no background/transparência aqui */
        document.documentElement.style.setProperty('--nav-shadow', 'rgba(255,255,255,.6)');
        // Remover classe dark do body para o footer
        document.body.classList.remove('dark');
      }
      const cs = getComputedStyle(document.documentElement);
      document.body.style.background = cs.getPropertyValue('--bg');
      document.body.style.color = cs.getPropertyValue('--text');
    };

    // Aplicar tema inicial baseado no estado do React
    setTheme(isDark);

    // Sincronizar o checkbox com o estado inicial
    if (el) {
      el.checked = isDark;
    }

    const onChange = (e: Event) => setTheme((e.target as HTMLInputElement).checked, false);
    el?.addEventListener('change', onChange);

    // Duplo clique para limpar preferência salva e seguir apenas o sistema
    let clickCount = 0;
    let clickTimer: NodeJS.Timeout;
    const handleDoubleClick = () => {
      clickCount++;
      if (clickCount === 1) {
        clickTimer = setTimeout(() => {
          clickCount = 0;
        }, 300);
      } else if (clickCount === 2) {
        clearTimeout(clickTimer);
        clickCount = 0;
        // Limpar localStorage para voltar a seguir apenas o sistema
        localStorage.removeItem('theme');
        const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setTheme(systemPrefersDark, true);
        if (el) {
          el.checked = systemPrefersDark;
        }
      }
    };
    el?.addEventListener('click', handleDoubleClick);

    // Listener para mudanças nas preferências do sistema
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      // Sempre aplicar mudanças do sistema (máxima prioridade)
      setTheme(e.matches, true); // true indica que é uma mudança do sistema
      if (el) {
        el.checked = e.matches;
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      el?.removeEventListener('change', onChange);
      el?.removeEventListener('click', handleDoubleClick);
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);

  // Controlar visibilidade da navbar
  useEffect(() => {
    let mouseTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const isTop = scrollY < 50;
      const isMobile = window.innerWidth <= 1280;

      setIsAtTop(isTop);

      // Em mobile, sempre visível
      if (isMobile) {
        setIsVisible(true);
        return;
      }

      // Desktop behavior
      if (scrollY > 100) {
        setIsVisible(true);
      } else if (scrollY < 50) {
        setIsVisible(false);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      const isMobile = window.innerWidth <= 1280;

      // Em mobile, ignorar mouse events
      if (isMobile) {
        return;
      }

      const y = e.clientY;

      if (y < 80) {
        setIsVisible(true);
        clearTimeout(mouseTimeout);
      } else {
        // Quando o rato sai da área, esconder imediatamente
        clearTimeout(mouseTimeout);
        mouseTimeout = setTimeout(() => {
          if (window.scrollY < 100) {
            setIsVisible(false);
          }
        }, 100); // Delay menor para resposta mais rápida
      }
    };

    const handleResize = () => {
      const mobile = window.innerWidth <= 1280;
      setIsMobile(mobile);
      if (mobile) {
        setIsVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    // Set initial state based on screen size
    handleResize();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      clearTimeout(mouseTimeout);
    };
  }, []);


  return (
    <>
      {/* Logo sempre visível na posição do header - apenas em desktop */}
      {!isMobile && (
        <div className={`logo-always-visible ${isDark ? 'dark-mode' : 'light-mode'}`}>
          <Logo href="/" className="logo-always-visible-link" />
        </div>
      )}

      <header className={`nav ${isDark ? 'dark-mode' : 'light-mode'} ${isVisible ? 'visible' : 'hidden'} ${isAtTop ? 'at-top' : ''}`}>
        <div className="container bar">
          {/* Logo for mobile/tablet - hidden on desktop */}
          <div className={`mobile-logo ${isDark ? 'dark-mode' : 'light-mode'}`}>
            <Logo href="/" className="mobile-logo-link" />
          </div>

          {/* Logo for desktop - hidden when logo-fixed is visible */}
          <div className={`desktop-logo ${isDark ? 'dark-mode' : 'light-mode'}`}>
            <Logo href="/" className="desktop-logo-link" />
          </div>

          <nav aria-label="principal" className={`desktop-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
            <ul>
              <li><a href="#como-funciona" className="mergeable" onClick={() => setIsMobileMenuOpen(false)}>Como Funciona</a></li>
              <li><a href="#recursos-ia" className="mergeable" onClick={() => setIsMobileMenuOpen(false)}>Recursos</a></li>
              <li><a href="#integracoes" className="mergeable" onClick={() => setIsMobileMenuOpen(false)}>Integrações</a></li>
              {/* <li><a href="#precos" className="mergeable" onClick={() => setIsMobileMenuOpen(false)}>Preços</a></li> */}
              <li><a href="#faq" className="mergeable" onClick={() => setIsMobileMenuOpen(false)}>FAQs</a></li>
            </ul>
          </nav>

          <div className="actions">
            <label title="Alternar tema" className="theme-toggle-wrapper mergeable">
              <span className="theme-icon">
                {isDark ? <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sun-icon lucide-sun"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg> : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-moon-icon lucide-moon"><path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401"/></svg>}
              </span>
              <input id="themeToggle" className="theme-toggle" type="checkbox" />
            </label>
            {/* <a className="btn ghost ripple mergeable" href="#login">Login</a> */}
            <a className="btn ripple cta-header mergeable" href="#comecar">Fale connosco</a>

            {/* Mobile menu button */}
                         <button
               className="mobile-menu-btn"
               onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
               aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
             >
              <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </div>

        {/* Mobile menu overlay */}
        {isMobileMenuOpen && (
          <div
            className="mobile-menu-overlay"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              background: 'var(--bg)',
              zIndex: 99999
            }}
            onClick={() => setIsMobileMenuOpen(false)}
          >
                         <nav
               className="mobile-menu"
               style={{
                 position: 'fixed',
                 top: 0,
                 left: 0,
                 right: 0,
                 bottom: 0,
                 width: '100vw',
                 height: '100vh',
                 background: 'var(--bg)',
                 display: 'flex',
                 flexDirection: 'column',
                 justifyContent: 'center',
                 alignItems: 'center',
                 zIndex: 100000,
                 padding: '80px 40px 40px'
               }}
               onClick={(e) => e.stopPropagation()}
             >
               {/* Close button */}
               <button
                 onClick={() => setIsMobileMenuOpen(false)}
                 style={{
                   position: 'absolute',
                   top: '20px',
                   right: '20px',
                   background: 'none',
                   border: 'none',
                   fontSize: '32px',
                   color: 'var(--text)',
                   cursor: 'pointer',
                   padding: '8px',
                   borderRadius: '50%',
                   width: '48px',
                   height: '48px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   transition: 'all 0.3s ease',
                   zIndex: 100001
                 }}
                 onMouseEnter={(e) => {
                   e.currentTarget.style.background = 'var(--muted)';
                   e.currentTarget.style.transform = 'scale(1.1)';
                 }}
                 onMouseLeave={(e) => {
                   e.currentTarget.style.background = 'transparent';
                   e.currentTarget.style.transform = 'scale(1)';
                 }}
                 aria-label="Fechar menu"
               >
                 ✕
               </button>
                             {/* Navigation Links */}
                             <ul
                 className="mobile-nav-links"
                                   style={{
                    display: 'block',
                    opacity: 1,
                    visibility: 'visible',
                    listStyle: 'none',
                    margin: '0',
                    padding: '0',
                    textAlign: 'center',
                    marginBottom: '60px'
                  }}
               >
                 <li style={{ marginBottom: '32px', opacity: '1', transform: 'translateY(0)' }}>
                   <a
                     href="#como-funciona"
                     onClick={() => setIsMobileMenuOpen(false)}
                     style={{
                       display: 'block',
                       padding: '16px 24px',
                       color: 'var(--text)',
                       textDecoration: 'none',
                       fontWeight: '600',
                       fontSize: '24px',
                       letterSpacing: '-0.5px',
                       borderRadius: '12px',
                       position: 'relative'
                     }}
                   >
                     Como Funciona
                   </a>
                 </li>
                 <li style={{ marginBottom: '32px', opacity: '1', transform: 'translateY(0)' }}>
                   <a
                     href="#recursos-ia"
                     onClick={() => setIsMobileMenuOpen(false)}
                     style={{
                       display: 'block',
                       padding: '16px 24px',
                       color: 'var(--text)',
                       textDecoration: 'none',
                       fontWeight: '600',
                       fontSize: '24px',
                       letterSpacing: '-0.5px',
                       borderRadius: '12px',
                       position: 'relative'
                     }}
                   >
                     Recursos
                   </a>
                 </li>
                 <li style={{ marginBottom: '32px', opacity: '1', transform: 'translateY(0)' }}>
                   <a
                     href="#integracoes"
                     onClick={() => setIsMobileMenuOpen(false)}
                     style={{
                       display: 'block',
                       padding: '16px 24px',
                       color: 'var(--text)',
                       textDecoration: 'none',
                       fontWeight: '600',
                       fontSize: '24px',
                       letterSpacing: '-0.5px',
                       borderRadius: '12px',
                       position: 'relative'
                     }}
                   >
                     Integrações
                   </a>
                 </li>
                 {/* <li style={{ marginBottom: '32px', opacity: '1', transform: 'translateY(0)' }}>
                   <a
                     href="#precos"
                     onClick={() => setIsMobileMenuOpen(false)}
                     style={{
                       display: 'block',
                       padding: '16px 24px',
                       color: 'var(--text)',
                       textDecoration: 'none',
                       fontWeight: '600',
                       fontSize: '24px',
                       letterSpacing: '-0.5px',
                       borderRadius: '12px',
                       position: 'relative'
                     }}
                   >
                     Preços
                   </a>
                 </li> */}
                 <li style={{ marginBottom: '32px', opacity: '1', transform: 'translateY(0)' }}>
                   <a
                     href="#faq"
                     onClick={() => setIsMobileMenuOpen(false)}
                     style={{
                       display: 'block',
                       padding: '16px 24px',
                       color: 'var(--text)',
                       textDecoration: 'none',
                       fontWeight: '600',
                       fontSize: '24px',
                       letterSpacing: '-0.5px',
                       borderRadius: '12px',
                       position: 'relative'
                     }}
                   >
                     FAQ
                   </a>
                 </li>
               </ul>

              {/* Actions */}
              <div className="mobile-menu-actions">
                <label title="Alternar tema" className="theme-toggle-wrapper">
                  <span className="theme-icon">
                    {isDark ? <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sun-icon lucide-sun"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg> : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-moon-icon lucide-moon"><path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401"/></svg>}
                  </span>
                  <span>Tema {isDark ? 'Claro' : 'Escuro'}</span>
                  <input
                    id="themeToggleMobile"
                    className="theme-toggle"
                    type="checkbox"
                    checked={isDark}
                    onChange={(e) => {
                      const themeToggle = document.getElementById('themeToggle') as HTMLInputElement;
                      if (themeToggle) {
                        themeToggle.checked = e.target.checked;
                        themeToggle.dispatchEvent(new Event('change'));
                      }
                    }}
                  />
                </label>
                <a className="btn ghost" href="#login">Login</a>
                <a className="btn" href="#comecar">Começar</a>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
